import { useCallback } from 'react'
import { Box, IconButton, InputBase, styled, Typography } from '@mui/material'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import hostConf from '$config/frontend'
// Components
import { Alert } from './alert'
import { LengthVerifier } from '$common/input/length-verifier'
// Localization
import { t } from '$setup/internationalization'

const StyledInputBase = styled(InputBase)<{ readOnly: boolean }>(
  ({ theme, readOnly }) => ({
    backgroundColor: theme.palette.veryVeryLightGray,

    padding: 'auto',
    boxSizing: 'border-box',

    width: '100%',
    height: '40px',
    ...theme.typography.subtitle3,

    border: `2px solid transparent`,
    borderRadius: '8px',

    ...(readOnly
      ? null
      : {
          '&.Mui-focused': {
            backgroundColor: theme.palette.white,
            borderColor: theme.palette.secondary.main,
          },
        }),

    '&.Mui-error': {
      borderColor: theme.palette.error.main,
    },
    '&.Mui-disabled': {
      color: theme.palette.gray,
      borderColor: theme.palette.lightGray,
      backgroundColor: theme.palette.white,
    },
    '& .MuiInputBase-input.Mui-disabled': {
      color: theme.palette.gray,
      '-webkit-text-fill-color': theme.palette.gray,
    },
  }),
)

const StyledTypography = styled(Typography)`
  margin-left: 16px;
`

interface Props {
  identifier: string
  isRepetitive?: boolean
  isChecking?: boolean
  hasAlert?: boolean
  disabled?: boolean
  readOnly?: boolean
  endIcon?: JSX.Element

  [propName: string]: unknown
  handleChange: (newValue: string) => void
  maxLength?: number
  showMaxLength?: boolean
  onEndIconClick?: () => unknown
}

export function LinkInput({
  isRepetitive = false,
  isChecking = false,
  identifier,
  handleChange,
  hasAlert = false,
  disabled = false,
  readOnly = false,
  endIcon = undefined,
  maxLength = undefined,
  showMaxLength = false,
  onEndIconClick,
  ...props
}: Props) {
  const handleInputChange = useCallback(
    (newValue: string) => {
      if (!maxLength || newValue.length <= maxLength) handleChange(newValue)
    },
    [handleChange, maxLength],
  )

  return (
    <>
      <StyledInputBase
        error={isRepetitive}
        dir="ltr"
        placeholder={!identifier && t('hallAccessibility.linkPlaceHolder')}
        value={identifier || ''}
        disabled={disabled}
        readOnly={readOnly}
        onChange={e => handleInputChange(e.target.value)}
        startAdornment={
          <StyledTypography variant="subtitle3" color="gray">
            {hostConf.FRONT_HOST}/
          </StyledTypography>
        }
        endAdornment={
          endIcon &&
          onEndIconClick && (
            <IconButton onClick={onEndIconClick}>{endIcon}</IconButton>
          )
        }
        {...props}
      />

      {showMaxLength && maxLength && (
        <Box sx={{ marginTop: '8px' }}>
          <LengthVerifier value={identifier} maxLength={maxLength} />
        </Box>
      )}

      {identifier &&
        hasAlert &&
        (isChecking ? (
          <Alert variant="info" text={t('linkInput.infoMessage')} />
        ) : isRepetitive ? (
          <Alert variant="error" text={t('linkInput.errorMessage')} />
        ) : (
          <Alert variant="success" text={t('linkInput.successMessage')} />
        ))}
    </>
  )
}
